import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal'; // Import the Modal component
import AIAssessmentViewer from "../../components/AIAssessmentViewer";
import Preloader from '../../components/Preloader';
import { convertMoodValue } from '../../utils/moodConverter';
import MeasurementDisplay from '../../components/MeasurementDisplay';

const ProgramAdminCases = () => {
  // Add missing function stubs to fix 'not defined' errors
  const loadCases = async (page = 1) => {
    try {
      // Use the existing search endpoint for all cases (no automatic exact search)
      const response = await api.get('/cases/view', {
        params: {
          per_page: casesPerPage,
          page,
          search: searchTerm,
          _t: Date.now()
        }
      });
      setCases(response.data.cases || []);
      setTotalCasesCount(response.data.total || 0);

      setLoading(false);
    } catch (error) {
      console.error("Error loading cases:", error);
      setLoading(false);
    }
  };

  // Separate function for exact case ID search (triggered by search button)
  const searchExactCaseId = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);

      // Check if search term is a valid numeric case ID
      const isExactCaseId = /^\d+$/.test(searchTerm.trim());

      if (isExactCaseId) {
        // Use the new program-admin specific exact search endpoint
        const response = await api.get('/cases/search/program-admin/exact', {
          params: { case_id: searchTerm.trim() }
        });

        if (response.data.success && response.data.case) {
          // Convert single case to array format for consistency with existing UI
          setCases([response.data.case]);
          setTotalCasesCount(1);
        } else {
          // Case not found - show user-friendly message
          setCases([]);
          setTotalCasesCount(0);
          setErrorModalTitle('Case Not Found');
          setErrorModalMessage(`No case found with case ID "${searchTerm}". Please check the case ID and try again.`);
          setShowErrorModal(true);
        }
      } else {
        // For non-numeric searches, show error message
        setErrorModalTitle('Invalid Case ID');
        setErrorModalMessage('Please enter a valid numeric case ID for exact search.');
        setShowErrorModal(true);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error("Error searching for exact case:", error);
      setLoading(false);
      if (error.response?.status === 404) {
        setErrorModalTitle('Case Not Found');
        setErrorModalMessage(`No case found with case ID "${searchTerm}". Please check the case ID and try again.`);
        setShowErrorModal(true);
      } else {
        setErrorModalTitle('Search Error');
        setErrorModalMessage('Error searching for case. Please try again.');
        setShowErrorModal(true);
      }
    }
  };

  const loadMedicalAdmins = async () => {
    try {
      const response = await api.get('/users?role=medical_admin');
      // keep only active / not-deleted medical admins (try common field names)
      const users = response.data.users || [];
      const visibleAdmins = users.filter(m => {
        const isActive = m.active === undefined || m.active === null ? true : Boolean(m.active);
        const notDeleted = !(m.deleted === true || m.is_deleted === true || !!m.deleted_at || !!m.deletedAt);
        return isActive && notDeleted;
      });
      setMedicalAdmins(visibleAdmins);
    } catch (err) {
      setMedicalAdmins([]);
    }
  };

  const loadAssignedCases = async () => {
    try {
      // Fetch all assignments to get their status
      const response = await api.get('/assignments');
      const raw = (response.data && response.data.assignments) || [];

      // normalize assignments so the rest of the code can rely on numeric fields
      const normalized = raw.map(a => {
        // normalize case id (support nested shapes)
        const caseIdCandidates = [
          a.case_id, a.caseId, a.assessment_id, a.assessmentId,
          a._case_id,
          // nested objects
          a.case && (a.case.id ?? a.case.case_id ?? a.caseId),
          a.assessment && (a.assessment.id ?? a.assessment.assessment_id ?? a.assessmentId)
        ];
        const foundCaseId = caseIdCandidates.find(v => v !== undefined && v !== null);
        const case_id = (foundCaseId === undefined || foundCaseId === null) ? undefined : Number(foundCaseId);

        // normalize medical admin id (support nested shapes and objects)
        let medCandidate = a.medical_admin_id ?? a.medicalAdminId ?? a.admin_id ?? a.user_id ?? a.adminId ?? a.userId ?? a._medical_admin_id;
        if (medCandidate === undefined || medCandidate === null) {
          // try nested objects
          if (a.medical_admin && (a.medical_admin.id ?? a.medical_admin.user_id)) {
            medCandidate = a.medical_admin.id ?? a.medical_admin.user_id;
          } else if (a.medicalAdmin && (a.medicalAdmin.id ?? a.medicalAdmin.user_id)) {
            medCandidate = a.medicalAdmin.id ?? a.medicalAdmin.user_id;
          } else if (a.admin && (a.admin.id ?? a.admin.user_id)) {
            medCandidate = a.admin.id ?? a.admin.user_id;
          } else if (a.user && (a.user.id ?? a.user.user_id)) {
            medCandidate = a.user.id ?? a.user.user_id;
          }
        }
        const medical_admin_id = (medCandidate === undefined || medCandidate === null) ? undefined : Number(medCandidate);

        return {
          ...a,
          // keep undefined when missing (avoid Number(null) -> 0 confusion)
          case_id: Number.isFinite(case_id) ? case_id : undefined,
          medical_admin_id: Number.isFinite(medical_admin_id) ? medical_admin_id : undefined
        };
      });

      setAssignedCases(normalized);
    } catch (error) {
      console.error("Error loading assigned cases:", error);
      setAssignedCases([]);
    }
  };

  const loadCaseStats = async () => {
    try {
      const response = await api.get('/cases/stats');
      if (response.data && response.data.stats) {
        setCaseStats(response.data.stats);
      }
    } catch (error) {
      console.error("Error loading case stats:", error);
    }
  };
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showBatchSelection, setShowBatchSelection] = useState(false);
  const [selectedCases, setSelectedCases] = useState([]);
  const [medicalAdmins, setMedicalAdmins] = useState([]);
  // CHANGED: allow selecting multiple medical admins
  const [selectedMedicalAdmins, setSelectedMedicalAdmins] = useState([]);
  // keep single for backwards compatibility in some flows
  const [selectedMedicalAdmin, setSelectedMedicalAdmin] = useState('');
  // Filter controls: select a single medical admin to inspect + assignment filter for that admin
  const [selectedFilterAdmin, setSelectedFilterAdmin] = useState(''); // single admin id used only for filtering
  const [adminAssignmentFilter, setAdminAssignmentFilter] = useState('all'); // 'all' | 'assigned' | 'unassigned'
  // admin-filtered cases from backend (used when selectedFilterAdmin is set)
  const [adminFilteredCases, setAdminFilteredCases] = useState([]);
  const [adminFilterLoading, setAdminFilterLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [expandedCaseId, setExpandedCaseId] = useState(null);
  const [expandedCaseDetails, setExpandedCaseDetails] = useState({});
  const [assignedCases, setAssignedCases] = useState([]);
  const [showAssignedCasesModal, setShowAssignedCasesModal] = useState(false);
  const [assignedCasesMessage, setAssignedCasesMessage] = useState('');
  const [showMedicalAdminAlert, setShowMedicalAdminAlert] = useState(false);
  const [showReassignAlert, setShowReassignAlert] = useState(false);
  const [reassignAlertMessage, setReassignAlertMessage] = useState(''); // eslint-disable-line no-unused-vars
  const [assigning, setAssigning] = useState(false);
  const [showUnassignConfirmation, setShowUnassignConfirmation] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalTitle, setErrorModalTitle] = useState('');
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [caseStats, setCaseStats] = useState({
    total_cases: 0,
    excellent_health_cases: 0,
    high_risk_cases: 0
  });

  // dropdown state & click outside handling
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const adminDropdownRef = useRef(null);

  useEffect(() => {
    loadCases(1);
    loadMedicalAdmins();
    loadAssignedCases();
    loadCaseStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When an admin is selected for filtering, fetch cases for that admin from backend.
  useEffect(() => {
    let cancelled = false;
    const fetchAdminFiltered = async () => {
      if (!selectedFilterAdmin) {
        setAdminFilteredCases([]);
        setAdminFilterLoading(false);
        return;
      }
      setAdminFilterLoading(true);
      try {
        const params = {
          medical_admin_id: Number(selectedFilterAdmin),
          assignment: adminAssignmentFilter || 'all',
          per_page: 10000, // fetch all matches for client-side pagination
          page: 1,
          search: searchTerm
        };
        
        // Pass server-side filter parameters to the backend
        if (ageMin !== undefined && ageMin !== '') params.age_min = ageMin;
        if (ageMax !== undefined && ageMax !== '') params.age_max = ageMax;
        if (genderFilter && genderFilter.length > 0) params.gender = genderFilter.join(',');
        if (overallGradeMin !== undefined && overallGradeMin !== '') params.overall_grade_min = parseFloat(overallGradeMin) * 10;
        if (overallGradeMax !== undefined && overallGradeMax !== '') params.overall_grade_max = parseFloat(overallGradeMax) * 10;
        if (bmiMin !== undefined && bmiMin !== '') params.bmi_min = parseFloat(bmiMin);
        if (bmiMax !== undefined && bmiMax !== '') params.bmi_max = parseFloat(bmiMax);
        if (bpMin !== undefined && bpMin !== '') params.bp_min = parseInt(bpMin);
        if (bpMax !== undefined && bpMax !== '') params.bp_max = parseInt(bpMax);
        if (completeProfileOnly) params.complete_profile = true;
        
        const resp = await api.get('/cases/filter-by-admin', { params });
        if (cancelled) return;
        setAdminFilteredCases(resp.data?.cases || []);
        // reset current page to 1 when admin filter changes
        setCurrentPage(1);
      } catch (err) {
        console.error('Error loading admin filtered cases:', err);
        setAdminFilteredCases([]);
      } finally {
        if (!cancelled) setAdminFilterLoading(false);
      }
    };

    fetchAdminFiltered();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterAdmin, adminAssignmentFilter, searchTerm]);

  // close admin dropdown on outside click
  useEffect(() => {
    const onDocumentClick = (e) => {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target)) {
        setShowAdminDropdown(false);
      }
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  const colorMap = {
    "#83D65C": "Excellent",
    "#5BC0DE": "Good",
    "#ffe366": "Good",
    "#ff944d": "Fair",
    "#ff6666": "High Risk",
    "#d9534f": "Critical"
  };

  // support multiple possible assignment admin id field names returned by backend
  const getAssignmentAdminId = (assignment) => {
    // prefer normalized field inserted by loadAssignedCases; fall back to nested shapes
    const v = assignment.medical_admin_id ??
      assignment.medicalAdminId ??
      assignment.admin_id ??
      assignment.user_id ??
      assignment.adminId ??
      assignment.userId ??
      assignment._medical_admin_id ??
      (assignment.medical_admin && (assignment.medical_admin.id ?? assignment.medical_admin.user_id)) ??
      (assignment.medicalAdmin && (assignment.medicalAdmin.id ?? assignment.medicalAdmin.user_id)) ??
      (assignment.admin && (assignment.admin.id ?? assignment.admin.user_id)) ??
      (assignment.user && (assignment.user.id ?? assignment.user.user_id));
    if (v === undefined || v === null) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const getAssignmentCaseId = (assignment) => {
    // prefer normalized field inserted by loadAssignedCases; fall back to nested shapes
    const v = assignment.case_id ??
      assignment.caseId ??
      assignment.assessment_id ??
      assignment.assessmentId ??
      assignment._case_id ??
      (assignment.case && (assignment.case.id ?? assignment.case.case_id ?? assignment.caseId)) ??
      (assignment.assessment && (assignment.assessment.id ?? assignment.assessment.assessment_id ?? assignment.assessmentId));
    if (v === undefined || v === null) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const isCaseAssigned = (caseId, medicalAdminId = null) => {
    if (!assignedCases || assignedCases.length === 0) return false;
    const targetCaseId = Number(caseId);
    if (Number.isNaN(targetCaseId)) return false;

    if (medicalAdminId !== null && medicalAdminId !== undefined && medicalAdminId !== '') {
      const targetAdminId = Number(medicalAdminId);
      if (Number.isNaN(targetAdminId)) return false;
      return assignedCases.some(a => {
        const aCase = getAssignmentCaseId(a);
        const aAdmin = getAssignmentAdminId(a);
        return aCase === targetCaseId && aAdmin === targetAdminId;
      });
    }

    return assignedCases.some(a => getAssignmentCaseId(a) === targetCaseId);
  };

  // reliable set of case IDs assigned to the currently selected filter admin
  const assignedCaseIdsForSelectedAdmin = useMemo(() => {
    const s = new Set();
    const adminId = selectedFilterAdmin ? Number(selectedFilterAdmin) : null;
    if (!adminId) return s;

    // prefer normalized assignedCases (from /assignments)
    (assignedCases || []).forEach(a => {
      const cid = getAssignmentCaseId(a);
      const aid = getAssignmentAdminId(a);
      if (cid !== undefined && aid === adminId) s.add(Number(cid));
    });

    // also include cases returned by the adminFilteredCases endpoint when appropriate
    if (adminFilteredCases && adminFilteredCases.length && adminAssignmentFilter === 'assigned') {
      adminFilteredCases.forEach(c => {
        if (c && c.id !== undefined && c.id !== null) s.add(Number(c.id));
      });
    }

    return s;
  }, [assignedCases, selectedFilterAdmin, adminFilteredCases, adminAssignmentFilter]);

  // returns true when a case is already assigned to ALL currently selected medical admins
  const isAssignedToAllSelectedAdmins = (caseId) => {
    if (!selectedMedicalAdmins || selectedMedicalAdmins.length === 0) return false;
    return selectedMedicalAdmins.every(adminId => isCaseAssigned(caseId, adminId));
  };

  // returns true when a case is assigned to ALL medical admins in the system (medicalAdmins list)
  const isAssignedToAllAdmins = (caseId) => {
    // consider only currently visible (active / not-deleted) medical admins
    const visibleAdmins = (medicalAdmins || []).filter(m => {
      const isActive = m.active === undefined || m.active === null ? true : Boolean(m.active);
      const notDeleted = !(m.deleted === true || m.is_deleted === true || !!m.deleted_at || !!m.deletedAt);
      return isActive && notDeleted;
    });

    const medAdminIds = visibleAdmins
      .map(m => Number(m.id))
      .filter(id => !Number.isNaN(id));

    if (medAdminIds.length === 0) return false;

    // collect unique admin ids already assigned for this case
    const assignedAdminIdsSet = new Set(
      (assignedCases || [])
        .filter(a => getAssignmentCaseId(a) === Number(caseId))
        .map(a => getAssignmentAdminId(a))
    );

    // assigned only if every visible medical admin has an assignment for this case
    return medAdminIds.every(id => assignedAdminIdsSet.has(id));
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [casesPerPage, setCasesPerPage] = useState(50);
  const [totalCasesCount, setTotalCasesCount] = useState(null);

  // Server-side filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bmiMin, setBmiMin] = useState('');
  const [bmiMax, setBmiMax] = useState('');
  const [bpMin, setBpMin] = useState(''); // Blood pressure minimum (systolic)
  const [bpMax, setBpMax] = useState(''); // Blood pressure maximum (systolic)
  const [bpMinDiastolic, setBpMinDiastolic] = useState(''); // Blood pressure minimum (diastolic)
  const [bpMaxDiastolic, setBpMaxDiastolic] = useState(''); // Blood pressure maximum (diastolic)
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [genderFilter, setGenderFilter] = useState([]); // array of strings
  const [overallGradeMin, setOverallGradeMin] = useState('');
  const [overallGradeMax, setOverallGradeMax] = useState('');
  const [completeProfileOnly, setCompleteProfileOnly] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  // Define calculateBMI function before it's used
  const calculateBMI = (weight, height, weightUnit = null, heightUnit = null) => {
    // normalize and guard
    if (weight === undefined || weight === null || height === undefined || height === null) return 'N/A';
    let w = Number(weight);
    let h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 'N/A';

    // Normalize units. Common stored units: height in cm, weight in kg.
    // If heightUnit explicitly set and looks like cm, convert to meters. Otherwise, if height value > 10 treat as cm.
    const hu = (heightUnit || '').toString().toLowerCase();
    if (hu.includes('cm') || h > 10) {
      h = h / 100.0; // convert cm to meters
    }

    // If weight unit provided and in lbs, convert to kg
    const wu = (weightUnit || '').toString().toLowerCase();
    if (wu.includes('lb') || wu.includes('lbs') || wu === 'lb') {
      w = w * 0.45359237;
    }

    const bmi = w / (h * h);
    if (!Number.isFinite(bmi)) return 'N/A';
    return bmi.toFixed(1);
  };

  // Filter cases by search, health status, and assignment status
  // Use adminFilteredCases when a single admin filter is active; otherwise use the full cases list.
  // Only use server-side filtering for advanced filters (age, BMI, etc.) - search is done client-side
  const hasServerFilters = useMemo(() => {
    const result = (
      ageMin !== '' || ageMax !== '' ||
      genderFilter.length > 0 || overallGradeMin !== '' || overallGradeMax !== '' ||
      bmiMin !== '' || bmiMax !== '' || bpMin !== '' || bpMax !== '' || bpMinDiastolic !== '' || bpMaxDiastolic !== '' ||
      completeProfileOnly
      // Note: selectedFilterAdmin is handled separately and doesn't prevent client-side filtering
      // Search is handled client-side like in Medical Admin
    );
    if (bpMin !== '' || bpMax !== '') {
      console.log(`[DEBUG] BP filter detected: bpMin='${bpMin}', bpMax='${bpMax}', hasServerFilters=${result}`);
    }
    return result;
  }, [ageMin, ageMax, genderFilter, overallGradeMin, overallGradeMax, bmiMin, bmiMax, bpMin, bpMax, bpMinDiastolic, bpMaxDiastolic, completeProfileOnly]);

  const filteredCases = (() => {
    // Use adminFilteredCases when admin filter is active, otherwise use cases
    const dataSource = selectedFilterAdmin ? adminFilteredCases : cases;
// Apply client-side filtering (health status, etc.) - search is now server-side
return dataSource.filter(case_ => {
  // Health status filtering
  let healthStatusMatch = false;
  if (filterStatus === 'all') healthStatusMatch = true;
  else if (filterStatus === 'excellent') healthStatusMatch = case_.score >= 80;
  else if (filterStatus === 'needs-attention') healthStatusMatch = case_.score >= 60 && case_.score < 80;
  else if (filterStatus === 'high-risk') healthStatusMatch = case_.score < 60;

  return healthStatusMatch;
});
  })();
  
  // when admin filtering is active, show a small loader in the header area
  useEffect(() => {
    if (selectedFilterAdmin) {
      // ensure cases state used by other parts stays consistent
      // (we already set adminFilteredCases separately)
    }
  }, [selectedFilterAdmin, adminFilteredCases]);

  // Calculate paginated cases
  // const isServerSide = !selectedFilterAdmin; // Use client-side pagination when filtering by admin - eslint-disable-line no-unused-vars
  const totalItems = selectedFilterAdmin ? filteredCases.length : totalCasesCount;
  const totalPages = Math.max(1, Math.ceil(totalItems / casesPerPage));

  // For client-side pagination when filtering by admin
  const paginatedCases = selectedFilterAdmin
    ? filteredCases.slice((currentPage - 1) * casesPerPage, currentPage * casesPerPage)
    : cases;

  // Handle page change
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Use client-side pagination when filtering by admin
      if (!selectedFilterAdmin) {
        if (hasServerFilters) {
          fetchCasesFromServer({ page });
        } else {
          loadCases(page);
        }
      }
      // For admin filtering, pagination is handled client-side
    }
  };

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(1);
    // For admin filtering, fetchAdminFiltered is called separately
    if (!selectedFilterAdmin) {
      // For non-admin filtering, reload cases when search changes
      if (hasServerFilters) {
        fetchCasesFromServer({ page: 1 });
      } else {
        loadCases(1);
      }
    }
    // For admin filtering, fetchAdminFiltered handles it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, selectedFilterAdmin, adminAssignmentFilter, overallGradeMin, overallGradeMax, hasServerFilters]);

  // Build query params and call backend /cases/view when server-side filters are applied
  const buildFilterParams = useCallback((overrides = {}) => {
    const params = { ...overrides };
    if (ageMin !== '') params.age_min = ageMin;
    if (ageMax !== '') params.age_max = ageMax;
    if (genderFilter && genderFilter.length > 0) params.gender = genderFilter.join(',');
    if (overallGradeMin !== '') params.overall_grade_min = parseFloat(overallGradeMin) * 10;
    if (overallGradeMax !== '') params.overall_grade_max = parseFloat(overallGradeMax) * 10;
    if (bmiMin !== '') params.bmi_min = parseFloat(bmiMin);
    if (bmiMax !== '') params.bmi_max = parseFloat(bmiMax);
    if (bpMin !== '') {
      params.bp_min = parseInt(bpMin);
      console.log('Setting bp_min parameter:', params.bp_min);
    }
    if (bpMax !== '') {
      params.bp_max = parseInt(bpMax);
      console.log('Setting bp_max parameter:', params.bp_max);
    }
    if (bpMinDiastolic !== '') {
      params.bp_min_diastolic = parseInt(bpMinDiastolic);
      console.log('Setting bp_min_diastolic parameter:', params.bp_min_diastolic);
    }
    if (bpMaxDiastolic !== '') {
      params.bp_max_diastolic = parseInt(bpMaxDiastolic);
      console.log('Setting bp_max_diastolic parameter:', params.bp_max_diastolic);
    }
    if (completeProfileOnly) params.complete_profile = true;
    // include selectedFilterAdmin as medical_admin_id to reuse backend endpoint when needed
    if (selectedFilterAdmin) params.medical_admin_id = selectedFilterAdmin;
    params.per_page = casesPerPage;
    params.page = overrides.page || currentPage || 1;
    console.log('Filter params being sent:', params);
    return params;
  }, [ageMin, ageMax, genderFilter, overallGradeMin, overallGradeMax, bmiMin, bmiMax, bpMin, bpMax, bpMinDiastolic, bpMaxDiastolic, completeProfileOnly, selectedFilterAdmin, casesPerPage, currentPage]);

  const fetchCasesFromServer = useCallback(async ({ page = 1 } = {}) => {
    setFilterLoading(true);
    try {
      const params = buildFilterParams({ page });
      console.log('[DEBUG] fetchCasesFromServer called with params:', params);
      const resp = await api.get('/cases/view', { params });
      const data = resp.data || {};
      setCases(data.cases || []);
      // backend returns total when paginated
      if (data.total !== undefined && data.total !== null) setTotalCasesCount(Number(data.total));
      setCurrentPage(Number(data.page || page));
      setCasesPerPage(Number(data.per_page || casesPerPage));
    } catch (err) {
      console.error('Failed to fetch filtered cases:', err);
    } finally {
      setFilterLoading(false);
    }
  }, [buildFilterParams, casesPerPage]);

  // Apply filters (user clicks Apply)
  const applyFilters = async () => {
    // basic validation: min <= max when both supplied
    if (ageMin !== '' && ageMax !== '' && Number(ageMin) > Number(ageMax)) {
      alert('Age minimum must be less than or equal to Age maximum');
      return;
    }
    if (bmiMin !== '' && bmiMax !== '' && Number(bmiMin) > Number(bmiMax)) {
      alert('BMI minimum must be less than or equal to BMI maximum');
      return;
    }
    if (bpMin !== '' && bpMax !== '' && Number(bpMin) > Number(bpMax)) {
      alert('BP systolic minimum must be less than or equal to BP systolic maximum');
      return;
    }
    if (bpMinDiastolic !== '' && bpMaxDiastolic !== '' && Number(bpMinDiastolic) > Number(bpMaxDiastolic)) {
      alert('BP diastolic minimum must be less than or equal to BP diastolic maximum');
      return;
    }
    if (overallGradeMin !== '' && overallGradeMax !== '' && Number(overallGradeMin) > Number(overallGradeMax)) {
      alert('Overall Grade minimum must be less than or equal to Overall Grade maximum');
      return;
    }
    setCurrentPage(1);
    // Always use server-side filtering when any filters are applied
    await fetchCasesFromServer({ page: 1 });
    setFiltersOpen(false);
  };

  const clearFilters = async () => {
    setAgeMin(''); setAgeMax(''); setGenderFilter([]);
    setOverallGradeMin(''); setOverallGradeMax(''); setBmiMin(''); setBmiMax('');
    setBpMin(''); setBpMax(''); setBpMinDiastolic(''); setBpMaxDiastolic(''); setCompleteProfileOnly(false);
    setSelectedFilterAdmin('');
    setCurrentPage(1);
    setTotalCasesCount(null);
    // Clear search term and reload default cases list
    setSearchTerm('');
    await loadCases();
  };

  const toggleCaseSelection = (caseId) => {
    if (selectedCases.includes(caseId)) {
      setSelectedCases(selectedCases.filter((id) => id !== caseId));
    } else {
      setSelectedCases([...selectedCases, caseId]);
    }
  };

  const handleCaseToggle = (caseId) => {
    toggleCaseSelection(caseId);
  };

  const handleSelectAll = () => {
    // Select / toggle only the cases visible on the current page (paginatedCases).
    const visibleIds = paginatedCases.map(c => c.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedCases.includes(id));
    if (allVisibleSelected) {
      // Deselect the visible cases only
      setSelectedCases(selectedCases.filter(id => !visibleIds.includes(id)));
    } else {
      // Add visible cases to current selection (union)
      const union = Array.from(new Set([...selectedCases, ...visibleIds]));
      setSelectedCases(union);
    }
  };
  
  const handleBatchAssign = () => {
    // require at least one medical admin selected before opening modal
    const hasAdminSelected = (selectedMedicalAdmins && selectedMedicalAdmins.length > 0) || !!selectedMedicalAdmin;
    if (!hasAdminSelected) {
      setShowMedicalAdminAlert(true);
      return;
    }

    if (selectedCases.length === 0) return;
    setShowBatchSelection(true);
  };

  const handleBatchUnassign = () => {
    if (selectedCases.length === 0) return;
    setShowUnassignConfirmation(true);
  };

  const toggleBatchSelection = () => { // eslint-disable-line no-unused-vars
    setShowBatchSelection(!showBatchSelection);
    if (showBatchSelection) {
      setSelectedCases([]);
      setSelectedMedicalAdmins([]);
      setSelectedMedicalAdmin('');
    }
  };

  // toggle individual medical admin selection (for multi-select)
  const toggleMedicalAdminSelection = (adminId) => {
    if (selectedMedicalAdmins.includes(String(adminId))) {
      setSelectedMedicalAdmins(selectedMedicalAdmins.filter(id => id !== String(adminId)));
    } else {
      setSelectedMedicalAdmins([...selectedMedicalAdmins, String(adminId)]);
    }
  };

  const confirmBatchAssign = async () => {
    // require at least one admin selected (either the single or multi)
    const adminsToSend = selectedMedicalAdmins.length > 0
      ? selectedMedicalAdmins.map(id => Number(id))
      : (selectedMedicalAdmin ? [Number(selectedMedicalAdmin)] : []);

    if (!adminsToSend || adminsToSend.length === 0) {
      setShowMedicalAdminAlert(true);
      return;
    }

    try {
      setAssigning(true);
      // send medical_admin_ids array to backend (backend supports array)
      const payload = {
        case_ids: selectedCases,
        medical_admin_ids: adminsToSend,
        assigned_by: user.id
      };

      const response = await api.post('/cases/assign-to-admin', payload);

      // build summary
      let summary = '';
      if (response?.data) {
        const created = response.data.created || [];
        const skipped = response.data.skipped || [];
        if (created.length) summary += `Created ${created.length} assignment(s). `;
        if (skipped.length) summary += `Skipped ${skipped.length} existing assignment(s). `;
      } else {
        summary = `Assigned ${selectedCases.length} case(s) to ${adminsToSend.length} admin(s).`;
      }

      setAssignedCasesMessage(summary);
      setShowAssignedCasesModal(true);

      // Reset selection and refresh
      setSelectedCases([]);
      setSelectedMedicalAdmins([]);
      setSelectedMedicalAdmin('');
      setShowBatchSelection(false);
      await loadCases();
      await loadAssignedCases();
      await loadCaseStats();
    } catch (err) {
      console.error('Assign error:', err);
      alert('Failed to assign cases. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const confirmBatchUnassign = async () => {
    // If no admins selected, unassign from all (don't send medical_admin_ids)
    const adminsToSend = selectedMedicalAdmins.length > 0
      ? selectedMedicalAdmins.map(id => Number(id))
      : (selectedMedicalAdmin ? [Number(selectedMedicalAdmin)] : null);

    try {
      setUnassigning(true);
      // send medical_admin_ids array to backend (backend supports array or null for all)
      const payload = {
        case_ids: selectedCases
      };
      if (adminsToSend) {
        payload.medical_admin_ids = adminsToSend;
      }

      const response = await api.post('/cases/unassign-from-admin', payload);

      // Build detailed summary message
      let message = response.data.message || 'Unassignment completed';

      // Add details about removed assignments
      if (response.data.removed && response.data.removed.length > 0) {
        message += '\n\n✅ Successfully Removed:';
        response.data.removed.forEach(item => {
          const adminName = medicalAdmins.find(a => a.id === item.medical_admin_id)?.name || `Admin ${item.medical_admin_id}`;
          message += `\n• Case ${item.case_id} from ${adminName} (${item.assignment_status})`;
        });
      }

      // Add details about skipped cases with reasons
      if (response.data.skipped && response.data.skipped.length > 0) {
        message += '\n\n❌ Could Not Unassign:';
        response.data.skipped.forEach(item => {
          message += `\n• Case ${item.case_id}: ${item.reason}`;
        });
      }

      // Add summary
      if (response.data.summary) {
        const { total_requested, removed_count, skipped_count } = response.data.summary;
        message += `\n\n📊 Summary: ${removed_count}/${total_requested} cases unassigned successfully`;
        if (skipped_count > 0) {
          message += `, ${skipped_count} skipped`;
        }
      }

      setAssignedCasesMessage(message);
      setShowAssignedCasesModal(true);

      // Reset selection and refresh
      setSelectedCases([]);
      setSelectedMedicalAdmins([]);
      setSelectedMedicalAdmin('');
      setShowUnassignConfirmation(false);
      await loadCases();
      await loadAssignedCases();
      await loadCaseStats();
    } catch (err) {
      console.error('Unassign error:', err);
      alert('Failed to unassign cases. Please try again.');
    } finally {
      setUnassigning(false);
    }
  };

  const getHealthColorClass = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  const toggleCardExpansion = async (caseId) => {
    if (expandedCaseId === caseId) {
      setExpandedCaseId(null);
      setExpandedCaseDetails({});
    } else {
      try {
        const response = await api.get(`/cases/${caseId}`);
        const details = response.data.case || {};
        // Ensure aiResponseData is present in expanded details
        if (!details.aiResponseData && details.aiResponse) {
          // fallback: try to parse if backend didn't provide
          try {
            details.aiResponseData = JSON.parse(details.aiResponse);
          } catch {}
        }
        setExpandedCaseDetails(details);
        setExpandedCaseId(caseId);
      } catch (error) {
        console.error("Error fetching case details:", error);
        alert('Failed to load case details. Please try again.');
      }
    }
  };

  const getColorName = (colorCode) => {
    return colorMap[colorCode] || "Unknown";
  };

  return (
    <ResearchLayout pageTitle="View All Cases">
      <div className="container-fluid">
        {/* Controls Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row align-items-center">
                  {/* Search and Assignment Filter */}
                  <div className="col-md-5 d-flex align-items-center gap-2">
                    <div className="input-group" style={{ minWidth: 0, flex: 1 }}>
                      <span className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter case ID for exact search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <button
                        className="btn btn-primary"
                        type="button"
                        onClick={searchExactCaseId}
                        disabled={!searchTerm.trim() || loading}
                      >
                        <i className="fas fa-search me-1"></i>
                        Search Exact
                      </button>
                    </div>
                    {/* Assignment Status Filter Dropdown */}
                  </div>
                  {/* Filter toggle and panel */}
                  <div className="col-md-3">
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setFiltersOpen(!filtersOpen)}
                      >
                        <i className="fas fa-filter me-1"></i>
                        Filters
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => { setSearchTerm(''); setFilterStatus('all'); clearFilters(); }}
                      >
                        Clear All
                      </button>
                    </div>
                    {filtersOpen && (
                      <div className="card mt-2 p-3 shadow-sm">
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label small">BMI Min</label>
                            <input className="form-control form-control-sm" type="number" value={bmiMin} onChange={e => setBmiMin(e.target.value)} />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">BMI Max</label>
                            <input className="form-control form-control-sm" type="number" value={bmiMax} onChange={e => setBmiMax(e.target.value)} />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">BP Min (Systolic)</label>
                            <input className="form-control form-control-sm" type="number" value={bpMin} onChange={e => setBpMin(e.target.value)} placeholder="e.g. 120" />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">BP Max (Systolic)</label>
                            <input className="form-control form-control-sm" type="number" value={bpMax} onChange={e => setBpMax(e.target.value)} placeholder="e.g. 140" />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">BP Min (Diastolic)</label>
                            <input className="form-control form-control-sm" type="number" value={bpMinDiastolic} onChange={e => setBpMinDiastolic(e.target.value)} placeholder="e.g. 80" />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">BP Max (Diastolic)</label>
                            <input className="form-control form-control-sm" type="number" value={bpMaxDiastolic} onChange={e => setBpMaxDiastolic(e.target.value)} placeholder="e.g. 90" />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">Age Min</label>
                            <input className="form-control form-control-sm" type="number" value={ageMin} onChange={e => setAgeMin(e.target.value)} />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">Age Max</label>
                            <input className="form-control form-control-sm" type="number" value={ageMax} onChange={e => setAgeMax(e.target.value)} />
                          </div>
                          <div className="col-12">
                            <label className="form-label small">Gender</label>
                            <div className="d-flex gap-2 flex-wrap">
                              {['Male','Female'].map(g => (
                                <button
                                  type="button"
                                  key={g}
                                  className={`btn btn-sm ${genderFilter.includes(g) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  onClick={() => {
                                    if (genderFilter.includes(g)) setGenderFilter(genderFilter.filter(x => x !== g));
                                    else setGenderFilter([...genderFilter, g]);
                                  }}
                                >{g}</button>
                              ))}
                            </div>
                          </div>
                          <div className="col-6">
                            <label className="form-label small">Overall Grade Min (%)</label>
                            <input
                              className="form-control form-control-sm"
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={overallGradeMin}
                              onChange={e => setOverallGradeMin(e.target.value)}
                              placeholder="e.g. 60"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label small">Overall Grade Max (%)</label>
                            <input
                              className="form-control form-control-sm"
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={overallGradeMax}
                              onChange={e => setOverallGradeMax(e.target.value)}
                              placeholder="e.g. 80"
                            />
                          </div>
                          <div className="col-12 d-flex align-items-center gap-2 mt-2">
                            <input id="completeProfileOnly" type="checkbox" checked={completeProfileOnly} onChange={e => setCompleteProfileOnly(e.target.checked)} />
                            <label htmlFor="completeProfileOnly" className="small mb-0">Only complete profiles</label>
                          </div>
                          <div className="col-12 d-flex justify-content-end mt-2">
                            <button type="button" className="btn btn-sm btn-secondary me-2" onClick={() => { setFiltersOpen(false); }}>Close</button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={applyFilters} disabled={filterLoading}>{filterLoading ? 'Applying...' : 'Apply Filters'}</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Health Status Filter */}
                  {/* <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Health Status</option>
                      <option value="excellent">Excellent Health</option>
                      <option value="needs-attention">Needs Attention</option>
                      <option value="high-risk">High Risk</option>
                    </select>
                  </div> */}

                  {/* Right-side action buttons */}
                  <div className="col-md-4 text-end">
                    <div className="d-inline-block me-2" style={{ minWidth: 220 }}>
                      {/* NEW: Admin filter (single select) + assignment filter for that admin */}
                      <div className="d-flex gap-2 mb-2">
                        <select
                          className="form-select form-select-sm"
                          value={selectedFilterAdmin}
                          onChange={e => {
                            const val = e.target.value;
                            setSelectedFilterAdmin(val);
                            // when an admin is selected, default to showing all cases for that admin
                            setAdminAssignmentFilter(val ? 'all' : 'all');
                          }}
                          style={{ minWidth: 180 }}
                        >
                          <option value="">Filter by medical admin (none)</option>
                          {medicalAdmins.map(admin => (
                            <option key={admin.id} value={admin.id}>
                              {admin.name} ({admin.email})
                            </option>
                          ))}
                        </select>

                        <select
                          className="form-select form-select-sm"
                          value={adminAssignmentFilter}
                          onChange={e => setAdminAssignmentFilter(e.target.value)}
                          style={{ minWidth: 160 }}
                          disabled={!selectedFilterAdmin}
                        >
                          <option value="all">All Assignments</option>
                          <option value="assigned">Assigned</option>
                          <option value="unassigned">Unassigned</option>
                        </select>
                      </div>
                       {/* Compact multi-select trigger - shows selected names */}
                       <div ref={adminDropdownRef} className="position-relative d-inline-block" style={{ width: '100%' }}>
                         <button
                           type="button"
                           className="btn btn-outline-secondary w-100 text-start"
                           onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                         >
                           {selectedMedicalAdmins.length > 0
                             ? `${selectedMedicalAdmins.length} admin(s) selected`
                             : (selectedMedicalAdmin ? (medicalAdmins.find(m => String(m.id) === String(selectedMedicalAdmin))?.name || 'Selected') : 'Select medical admin(s)')}
                           <span className="float-end"><i className="fas fa-caret-down"></i></span>
                         </button>

                         {showAdminDropdown && (
                           <div
                             className="shadow-sm bg-white border position-absolute"
                             style={{ right: 0, left: 0, zIndex: 1200, maxHeight: 260, overflowY: 'auto', marginTop: 6, padding: 8 }}
                           >
                             {medicalAdmins.length === 0 && <div className="text-muted small px-2">No medical admins</div>}
                             {medicalAdmins.map(admin => {
                               const checked = selectedMedicalAdmins.includes(String(admin.id));
                               return (
                                 <label key={admin.id} className="d-flex align-items-center gap-2 px-2 py-1" style={{ cursor: 'pointer' }}>
                                   <input
                                     type="checkbox"
                                     checked={checked}
                                     onChange={() => toggleMedicalAdminSelection(admin.id)}
                                   />
                                   <div className="small">
                                     <div className="fw-semibold">{admin.name}</div>
                                     <div className="text-muted small">{admin.email}</div>
                                   </div>
                                 </label>
                               );
                             })}
                             <div className="d-flex justify-content-between mt-2">
                               <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelectedMedicalAdmins([]); setSelectedMedicalAdmin(''); }}>
                                 Clear
                               </button>
                               <button className="btn btn-sm btn-primary" onClick={() => setShowAdminDropdown(false)}>
                                 Done
                               </button>
                             </div>
                           </div>
                         )}
                       </div>
                     </div>

                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={handleSelectAll}
                    >
                      <i className="fas fa-check-square me-2"></i>
                      {paginatedCases.length > 0 && paginatedCases.every(c => selectedCases.includes(c.id)) ? 'Deselect Page' : 'Select Page'}
                    </button>
                    <button
                      className="btn btn-warning me-2"
                      onClick={handleBatchAssign}
                      disabled={
                        selectedCases.length === 0 ||
                        !((selectedMedicalAdmins && selectedMedicalAdmins.length > 0) || Boolean(selectedMedicalAdmin))
                      }
                    >
                      <i className="fas fa-tasks me-2"></i>
                      Batch Assign ({selectedCases.length})
                    </button>
                    <div className="d-flex flex-column align-items-end">
                      <button
                        className="btn btn-danger"
                        onClick={handleBatchUnassign}
                        disabled={selectedCases.length === 0}
                        title={selectedCases.length === 0 ? "Select cases to unassign" : "Unassign selected cases from medical admin(s)"}
                      >
                        <i className="fas fa-times me-2"></i>
                        Unassign ({selectedCases.length})
                      </button>
                      {selectedCases.length > 0 && (
                        <small className="text-muted mt-1">
                          Tip: Filter by medical admin first to see assigned cases
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
           <div className="col-lg-3 col-md-6 mb-3">
             <div className="card border-0 shadow-sm bg-primary text-white">
               <div className="card-body text-center">
                 <i className="fas fa-users fa-2x mb-2"></i>
                 <h4>{caseStats.total_cases}</h4>
                 <p className="mb-0">Total Cases</p>
               </div>
             </div>
           </div>
           <div className="col-lg-3 col-md-6 mb-3">
             <div className="card border-0 shadow-sm bg-success text-white">
               <div className="card-body text-center">
                 <i className="fas fa-heart fa-2x mb-2"></i>
                 <h4>{caseStats.excellent_health_cases}</h4>
                 <p className="mb-0">Excellent Health</p>
               </div>
             </div>
           </div>
           <div className="col-lg-3 col-md-6 mb-3">
             <div className="card border-0 shadow-sm bg-warning text-white">
               <div className="card-body text-center">
                 <i className="fas fa-exclamation-triangle fa-2x mb-2"></i>
                 <h4>{caseStats.total_cases - caseStats.excellent_health_cases - caseStats.high_risk_cases}</h4>
                 <p className="mb-0">Needs Attention</p>
               </div>
             </div>
           </div>
           <div className="col-lg-3 col-md-6 mb-3">
             <div className="card border-0 shadow-sm bg-danger text-white">
               <div className="card-body text-center">
                 <i className="fas fa-heartbeat fa-2x mb-2"></i>
                 <h4>{caseStats.high_risk_cases}</h4>
                 <p className="mb-0">High Risk</p>
               </div>
             </div>
           </div>
         </div>

        {/* Cases Grid */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-database me-2"></i>
                  All Research Cases ({paginatedCases.length})
                </h5>
              </div>
              <div className="card-body">
                {filteredCases.length > 0 ? (
                  <>
                    <div className="row">
                      {paginatedCases.map((case_) => (
                        <div key={case_.id} className="col-lg-4 col-md-6 mb-4">
                          <div className={`card h-100 ${selectedCases.includes(case_.id) ? 'border-primary shadow' : 'border'}`}>
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`case-${case_.id}`}
                                  checked={selectedCases.includes(case_.id)}
                                  onChange={() => handleCaseToggle(case_.id)}
                                  // only disable when case is already assigned to every selected medical admin
                                  disabled={selectedMedicalAdmins && selectedMedicalAdmins.length > 0 ? isAssignedToAllSelectedAdmins(case_.id) : false}
                                />
                                <label className="form-check-label fw-bold" htmlFor={`case-${case_.id}`}>
                                  {case_.id}
                                </label>
                              </div>
                              <div>
                                <span className={`badge ${getScoreBadgeClass(case_.score)}`}>
                                  Score: {case_.score}
                                </span>
                                {isAssignedToAllAdmins(case_.id) && (
                                  <span className="badge bg-danger ms-2">Assigned</span>
                                )}
                                {selectedFilterAdmin && (
                                  (assignedCaseIdsForSelectedAdmin.has(Number(case_.id)) ? (
                                    <span className="badge bg-danger ms-2">Assigned</span>
                                  ) : (
                                    <span className="badge bg-secondary ms-2">Not assigned</span>
                                  ))
                                 )}
                              </div>
                            </div>
                            <div className="card-body">
                              {/* Demographics */}
                              <div className="row mb-3">
                                <div className="col-6">
                                  <div className="small text-muted">Age</div>
                                  <div className="fw-bold small">{case_.age || 'N/A'}</div>
                                </div>
                                <div className="col-6">
                                  <div className="small text-muted">Gender</div>
                                  <div className="fw-bold small">{case_.gender || 'N/A'}</div>
                                </div>
                              </div>

                              {/* Health Predictions */}
                              <div className="row mb-3">
                                <div className="col-6">
                                  <div className="small text-muted">Physical</div>
                                  <div className={`fw-bold small ${getHealthColorClass(case_.physicalHealthPrediction)}`}>
                                    {getColorName(case_.physicalHealthPrediction)}
                                  </div>
                                </div>
                                <div className="col-6">
                                  <div className="small text-muted">Mental</div>
                                  <div className={`fw-bold small ${getHealthColorClass(case_.mentalHealthPrediction)}`}>
                                    {getColorName(case_.mentalHealthPrediction)}
                                  </div>
                                </div>
                              </div>

                              {/* Basic Metrics */}
                              <div className="row mb-3">
                                <div className="col-3">
                                  <div className="small text-muted">Weight</div>
                                  <div className="fw-bold small">
                                    <MeasurementDisplay
                                      measurementType="weight"
                                      value={case_.weight}
                                      originalUnit={case_.weightUnit}
                                      showLabel={false}
                                    />
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="small text-muted">Height</div>
                                  <div className="fw-bold small">
                                    <MeasurementDisplay
                                      measurementType="height"
                                      value={case_.height}
                                      originalUnit={case_.heightUnit}
                                      showLabel={false}
                                    />
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="small text-muted">BP</div>
                                  <div className="fw-bold small">
                                    {(case_.systolicBloodPressure != null && case_.diastolicBloodPressure != null)
                                      ? `${case_.systolicBloodPressure}/${case_.diastolicBloodPressure}`
                                      : 'N/A'}
                                  </div>
                                </div>
                                <div className="col-3">
                                  <div className="small text-muted">BMI</div>
                                  <div className="fw-bold small">{calculateBMI(case_.weight, case_.height, case_.weightUnit || case_.weight_unit, case_.heightUnit || case_.height_unit)}</div>
                                </div>
                              </div>

                              {/* Risk Factors */}
                              <div className="mb-3">
                                <div className="d-flex flex-wrap gap-1">
                                  {case_.isSmoker && (
                                    <span className="badge bg-danger">Smoker</span>
                                  )}
                                  {case_.isDrinker && (
                                    <span className="badge bg-warning">Drinks</span>
                                  )}
                                  {case_.diagnosedChronicDisease !== 'None' && (
                                    <span className="badge bg-info">Chronic Disease</span>
                                  )}
                                  <span className={`badge ${
                                    case_.activeDailyLifestyleLevel === 'High' ? 'bg-success' :
                                    case_.activeDailyLifestyleLevel === 'Moderate' ? 'bg-primary' : 'bg-secondary'
                                  }`}>
                                    {case_.activeDailyLifestyleLevel} Activity
                                  </span>
                                </div>
                                {/* Overall Health Grade removed from card view (kept in expanded Full AI Assessment) */}
                              </div>

                              {/* Expanded Details */}
                              {expandedCaseId === case_.id && (
                                <div className="mt-3">
                                  <hr />
                                  <p>
                                    <strong>Full AI Assessment:</strong>
                                    {expandedCaseDetails.aiResponseData ? (
                                      <AIAssessmentViewer data={expandedCaseDetails.aiResponseData} />
                                    ) : (
                                      ' N/A'
                                    )}
                                  </p>
                                  <p><strong>Resting Heart Rate:</strong> {expandedCaseDetails.restingHeartRate != null ? expandedCaseDetails.restingHeartRate : 'N/A'}</p>
                                  <p><strong>Systolic Blood Pressure:</strong> {expandedCaseDetails.systolicBloodPressure != null ? expandedCaseDetails.systolicBloodPressure : 'N/A'}</p>
                                  <p><strong>Diastolic Blood Pressure:</strong> {expandedCaseDetails.diastolicBloodPressure != null ? expandedCaseDetails.diastolicBloodPressure : 'N/A'}</p>
                                  <p><strong>Weight:</strong> <MeasurementDisplay measurementType="weight" value={expandedCaseDetails.weight} originalUnit={expandedCaseDetails.weightUnit} showLabel={false} /></p>
                                  <p><strong>Height:</strong> <MeasurementDisplay measurementType="height" value={expandedCaseDetails.height} originalUnit={expandedCaseDetails.heightUnit} showLabel={false} /></p>
                                  <p><strong>Waist:</strong> <MeasurementDisplay measurementType="circumference" value={expandedCaseDetails.waist} originalUnit={expandedCaseDetails.waistUnit} showLabel={false} /></p>
                                  <p><strong>Hip:</strong> <MeasurementDisplay measurementType="circumference" value={expandedCaseDetails.hip} originalUnit={expandedCaseDetails.hipUnit} showLabel={false} /></p>
                                  <p><strong>Highest Education:</strong> {expandedCaseDetails.highestEducation || 'N/A'}</p>
                                  <p><strong>Is Smoker:</strong> {expandedCaseDetails.isSmoker != null ? (expandedCaseDetails.isSmoker ? 'Yes' : 'No') : 'N/A'}</p>
                                  <p><strong>Cigarettes Per Day:</strong> {expandedCaseDetails.cigarettesPerDay != null ? expandedCaseDetails.cigarettesPerDay : 'N/A'}</p>
                                  <p><strong>Years Smoked:</strong> {expandedCaseDetails.yearsSmoked != null ? expandedCaseDetails.yearsSmoked : 'N/A'}</p>
                                  <p><strong>Is Drinker:</strong> {expandedCaseDetails.isDrinker != null ? (expandedCaseDetails.isDrinker ? 'Yes' : 'No') : 'N/A'}</p>
                                  <p><strong>Bottles Per Day:</strong> {expandedCaseDetails.bottlesPerDay != null ? expandedCaseDetails.bottlesPerDay : 'N/A'}</p>
                                  <p><strong>Drinking Days Per Week:</strong> {expandedCaseDetails.drinkingDaysPerWeek != null ? expandedCaseDetails.drinkingDaysPerWeek : 'N/A'}</p>
                                  <p><strong>Active Daily Lifestyle Level:</strong> {expandedCaseDetails.activeDailyLifestyleLevel || 'N/A'}</p>
                                  <p><strong>Well Sleep Level:</strong> {expandedCaseDetails.wellSleepLevel || 'N/A'}</p>
                                  <p><strong>Stress Frequency:</strong> {expandedCaseDetails.stressFrequency === 'Never' || expandedCaseDetails.stressFrequency === '0' || expandedCaseDetails.stressFrequency === 0 ? 'Never' :
                                    expandedCaseDetails.stressFrequency === 'Rarely' || expandedCaseDetails.stressFrequency === 'Rare' || expandedCaseDetails.stressFrequency === '1' || expandedCaseDetails.stressFrequency === 1 ? 'Sometimes' :
                                    expandedCaseDetails.stressFrequency === 'Sometimes' || expandedCaseDetails.stressFrequency === '2' || expandedCaseDetails.stressFrequency === 2 ? 'Many times' :
                                    expandedCaseDetails.stressFrequency === 'Often' || expandedCaseDetails.stressFrequency === 'Very Often' || expandedCaseDetails.stressFrequency === '3' || expandedCaseDetails.stressFrequency === 3 ? 'Always' :
                                    expandedCaseDetails.stressFrequency || 'N/A'}</p>
                                  <p><strong>Loss of Interest/Appetite Frequency:</strong> {expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Never' || expandedCaseDetails.lossOfInterestAppetiteFrequency === '0' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 0 ? 'Never' :
                                    expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Rarely' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Rare' || expandedCaseDetails.lossOfInterestAppetiteFrequency === '1' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 1 ? 'Sometimes' :
                                    expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Sometimes' || expandedCaseDetails.lossOfInterestAppetiteFrequency === '2' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 2 ? 'Many times' :
                                    expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Often' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 'Very Often' || expandedCaseDetails.lossOfInterestAppetiteFrequency === '3' || expandedCaseDetails.lossOfInterestAppetiteFrequency === 3 ? 'Always' :
                                    expandedCaseDetails.lossOfInterestAppetiteFrequency || 'N/A'}</p>
                                  <p><strong>Fatigue Frequency:</strong> {expandedCaseDetails.fatigueFrequency === 'Never' || expandedCaseDetails.fatigueFrequency === '0' || expandedCaseDetails.fatigueFrequency === 0 ? 'Never' :
                                    expandedCaseDetails.fatigueFrequency === 'Rarely' || expandedCaseDetails.fatigueFrequency === 'Rare' || expandedCaseDetails.fatigueFrequency === '1' || expandedCaseDetails.fatigueFrequency === 1 ? 'Sometimes' :
                                    expandedCaseDetails.fatigueFrequency === 'Sometimes' || expandedCaseDetails.fatigueFrequency === '2' || expandedCaseDetails.fatigueFrequency === 2 ? 'Many times' :
                                    expandedCaseDetails.fatigueFrequency === 'Often' || expandedCaseDetails.fatigueFrequency === 'Very Often' || expandedCaseDetails.fatigueFrequency === '3' || expandedCaseDetails.fatigueFrequency === 3 ? 'Always' :
                                    expandedCaseDetails.fatigueFrequency || 'N/A'}</p>
                                  <p><strong>Mood:</strong> {expandedCaseDetails.mood != null ? convertMoodValue(expandedCaseDetails.mood) : 'N/A'}</p>
                                  <p><strong>Diagnosed Chronic Disease:</strong> {expandedCaseDetails.diagnosedChronicDisease || 'N/A'}</p>
                                  <p><strong>Family Member Diagnosed Chronic Disease:</strong> {expandedCaseDetails.familyMemberDiagnosedChronicDisease || 'N/A'}</p>
                                  <p><strong>Blood Glucose:</strong> {expandedCaseDetails.bloodGlucose != null ? expandedCaseDetails.bloodGlucose : 'N/A'} {expandedCaseDetails.bloodGlucoseUnit != null ? expandedCaseDetails.bloodGlucoseUnit : 'N/A'}</p>
                                  <p><strong>Oxygen Saturation Level:</strong> {expandedCaseDetails.oxygenSaturationLevel != null ? expandedCaseDetails.oxygenSaturationLevel : 'N/A'}</p>
                                  <p><strong>Last Month Pain:</strong> {expandedCaseDetails.lastMonthPain != null ? expandedCaseDetails.lastMonthPain : 'N/A'}</p>
                                  <p><strong>Last Month Diminished Vision:</strong> {expandedCaseDetails.lastMonthDiminishedVision != null ? expandedCaseDetails.lastMonthDiminishedVision : 'N/A'}</p>
                                  <p><strong>Last Month Diminished Hearing:</strong> {expandedCaseDetails.lastMonthDiminishedHearing != null ? expandedCaseDetails.lastMonthDiminishedHearing : 'N/A'}</p>
                                  {/* Add more details here as needed */}
                                </div>
                              )}
                            </div>
                            <div className="card-footer bg-light">
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                  Completion: {case_.assessmentCompletionPercentage}%
                                </small>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => toggleCardExpansion(case_.id)}
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  {expandedCaseId === case_.id ? 'Hide Details' : 'View Details'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Pagination Controls */}
                    <div className="mt-4">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={goToPage}
                        showInfo={true}
                        totalItems={totalItems}
                        itemsPerPage={casesPerPage}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Cases Found</h5>
                    <p className="text-muted">Try adjusting your search criteria.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {(assigning || unassigning) && <Preloader />}
        {showBatchSelection && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-warning text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-tasks me-2"></i>
                    Batch Case Assignment
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowBatchSelection(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    You are about to prepare <strong>{selectedCases.length} cases</strong> for medical admin assignment.
                  </div>

                  <div className="mb-3">
                    <h6>Selected Cases:</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {selectedCases.map(id => {
                        const case_ = cases.find(c => c.id === id);
                        return (
                          <span key={id} className="badge bg-primary">
                            {case_?.id}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Medical Admin Selection (multi-select friendly, responsive) */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-shield me-2 text-primary"></i>
                      Assigning to
                    </label>
                    <div>
                      {selectedMedicalAdmins && selectedMedicalAdmins.length > 0 ? (
                        <div>
                          {selectedMedicalAdmins.map(id => {
                            const m = medicalAdmins.find(a => String(a.id) === String(id));
                            return (
                              <div key={id} className="fw-bold">
                                {m?.name || id} <small className="text-muted">({m?.email || ''})</small>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-muted">No medical admin selected — select admins from the top selector before confirming.</div>
                      )}
                    </div>
                  </div>

                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    This action will make these cases available for medical admin to assign to doctors with protocols.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowBatchSelection(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={confirmBatchAssign}
                    disabled={assigning}
                  >
                    <i className="fas fa-check me-2"></i>
                    {assigning ? 'Assigning...' : 'Confirm Batch Selection'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUnassignConfirmation && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    <i className="fas fa-times me-2"></i>
                    Unassign Cases from Medical Admin
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowUnassignConfirmation(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    You are about to unassign <strong>{selectedCases.length} cases</strong> from medical admin(s).
                  </div>

                  <div className="mb-3">
                    <h6>Selected Cases:</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {selectedCases.map(id => {
                        const case_ = cases.find(c => c.id === id);
                        return (
                          <span key={id} className="badge bg-primary">
                            {case_?.id}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Medical Admin Selection (multi-select friendly, responsive) */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-shield me-2 text-primary"></i>
                      Unassigning from
                    </label>
                    <div>
                      {selectedMedicalAdmins && selectedMedicalAdmins.length > 0 ? (
                        <div>
                          {selectedMedicalAdmins.map(id => {
                            const m = medicalAdmins.find(a => String(a.id) === String(id));
                            return (
                              <div key={id} className="fw-bold">
                                {m?.name || id} <small className="text-muted">({m?.email || ''})</small>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-info">
                          <strong>All medical admins</strong> - No specific admins selected, will unassign from all medical admins assigned to these cases.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    This action will remove the assignment of these cases from the selected medical admin(s). The cases will no longer be available to these admins.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowUnassignConfirmation(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmBatchUnassign}
                    disabled={unassigning}
                  >
                    <i className="fas fa-check me-2"></i>
                    {unassigning ? 'Unassigning...' : 'Confirm Unassign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assigned Cases Modal */}
        <Modal
          isOpen={showAssignedCasesModal}
          onClose={() => setShowAssignedCasesModal(false)}
          title="Unassignment Confirmation"
        >
          {assignedCasesMessage}
        </Modal>

        {/* Medical Admin Alert Modal */}
        <Modal
          isOpen={showMedicalAdminAlert}
          onClose={() => setShowMedicalAdminAlert(false)}
          title="Medical Admin Required"
        >
          Please select a medical admin.
        </Modal>

        {/* Reassign Alert Modal */}
        <Modal
          isOpen={showReassignAlert}
          onClose={() => setShowReassignAlert(false)}
          title="Cannot Re-assign Cases"
        >
          {reassignAlertMessage}
        </Modal>

        {/* Error Modal */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title={errorModalTitle}
        >
          {errorModalMessage}
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default ProgramAdminCases;